/**
 * Bounded context: Onboarding — Rules (D7 / ADR-0014)
 *
 * ⚠️ GATE CONSTITUCIONAL ⚠️
 *
 * A ordem dos 5 turnos é CANÔNICA (CC-08: motivação cultivada; D7: ≤5min).
 * Qualquer mudança aqui passa pelo Constitutional Guardian.
 *
 * - R6 (Princípio V): nenhuma transição é iniciada pelo sistema; tudo
 *   depende de clique do usuário.
 * - R5: copy é sempre convite ("Você pode...", "Quando quiser...").
 * - CC-04: zero urgência artificial, zero "não perca", "tempo limitado".
 *
 * Regra de timing (D7): onboarding deve ser concluído em ≤5min. Validação
 * é **interna** (rastreamento de timestamps), **não exibida** ao usuário
 * (CC-01: zero contador).
 */

import type { OnboardingDraft, OnboardingStep } from "./types";
import { INITIAL_DRAFT, ONBOARDING_STEPS } from "./types";

// Re-export para conveniência (componentes podem importar tudo de "./rules").
export { INITIAL_DRAFT };
export type { OnboardingDraft };

/** Tipo mutável para construir drafts parciais (TypeScript's Partial<T> preserva `readonly` em TS 5.x). */
export type WritableDraft = {
  -readonly [K in keyof OnboardingDraft]: OnboardingDraft[K];
};

/** Eventos que disparam transições no onboarding. */
export type OnboardingEvent =
  | { type: "INITIALIZE"; startedAt: number }
  | { type: "NEXT"; draft: Partial<WritableDraft> }
  | { type: "BACK" }
  | { type: "SKIP" }
  | { type: "FINISH" };

/** Resultado da transição: novo step + draft atualizado. */
export interface OnboardingTransition {
  readonly nextStep: OnboardingStep | "done";
  readonly nextDraft: OnboardingDraft;
  /** Elapsed time em ms (para validação D7 ≤5min). */
  readonly elapsedMs: number;
  /** True se excedeu D7 (warning, não bloqueia conclusão). */
  readonly exceedsD7: boolean;
}

/** Limite D7: 5 minutos em ms. */
export const D7_LIMIT_MS = 5 * 60 * 1000;

/** Aplica um evento de onboarding e retorna o novo estado. */
export function transition(
  currentStep: OnboardingStep | "intro" | "done",
  currentDraft: OnboardingDraft,
  startedAt: number,
  event: OnboardingEvent,
): OnboardingTransition {
  const now = Date.now();
  const elapsedMs = now - startedAt;
  const exceedsD7 = elapsedMs > D7_LIMIT_MS;

  switch (event.type) {
    case "INITIALIZE":
      return {
        nextStep: "intro",
        nextDraft: INITIAL_DRAFT,
        elapsedMs: 0,
        exceedsD7: false,
      };
    case "NEXT": {
      const merged: OnboardingDraft = {
        ...currentDraft,
        ...event.draft,
      } as OnboardingDraft;
      const idx = ONBOARDING_STEPS.indexOf(currentStep as OnboardingStep);
      if (idx === -1 || idx >= ONBOARDING_STEPS.length - 1) {
        return {
          nextStep: "done",
          nextDraft: merged,
          elapsedMs,
          exceedsD7,
        };
      }
      const nextStep = ONBOARDING_STEPS[idx + 1] ?? "done";
      return { nextStep, nextDraft: merged, elapsedMs, exceedsD7 };
    }
    case "BACK": {
      const idx = ONBOARDING_STEPS.indexOf(currentStep as OnboardingStep);
      if (idx <= 0) {
        return { nextStep: currentStep, nextDraft: currentDraft, elapsedMs, exceedsD7 };
      }
      const prevStep = ONBOARDING_STEPS[idx - 1] ?? "intro";
      return { nextStep: prevStep, nextDraft: currentDraft, elapsedMs, exceedsD7 };
    }
    case "SKIP": {
      // SKIP só permitido em turnos opcionais (personal). Outros turnos
      // exigem NEXT (CC-05: sem penalidade; só pula o que é opcional).
      if (currentStep !== "personal") {
        return { nextStep: currentStep, nextDraft: currentDraft, elapsedMs, exceedsD7 };
      }
      const idx = ONBOARDING_STEPS.indexOf(currentStep);
      const nextStep = ONBOARDING_STEPS[idx + 1] ?? "done";
      return {
        nextStep,
        nextDraft: { ...currentDraft, personal: "" },
        elapsedMs,
        exceedsD7,
      };
    }
    case "FINISH": {
      // Só termina a partir de "mnemonic" (último turno).
      if (currentStep !== "mnemonic") {
        return { nextStep: currentStep, nextDraft: currentDraft, elapsedMs, exceedsD7 };
      }
      return {
        nextStep: "done",
        nextDraft: { ...currentDraft, mnemonicConfirmed: true },
        elapsedMs,
        exceedsD7,
      };
    }
    default: {
      // Exhaustiveness: se um novo event for adicionado, `event` deixa de
      // ser `never` e o TypeScript aponta o erro de compilação.
      const _exhaustive: never = event;
      // Use _exhaustive para satisfazer strict mode (referência simbólica).
      void _exhaustive;
      return { nextStep: currentStep, nextDraft: currentDraft, elapsedMs, exceedsD7 };
    }
  }
}

/** Estado inicial puro para testes. */
export const INITIAL_STEP: OnboardingStep = "intro";
